import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatPrice, type EditorialContent, type Locale, type Product } from "@nh/shared";
import { maxQuantityFor, useCart } from "@/features/cart";
import { cn } from "@/shared/lib/cn";
import { numberLocaleOf, type ResolvedPrice } from "../shop.catalog";
import { PackageUpsellDialog } from "./PackageUpsellDialog";

export type ProductClosingContent = NonNullable<EditorialContent["closing"]>;
type ProductClosingTheme = NonNullable<ProductClosingContent["theme"]>;

export interface ProductClosingProps {
  closing: ProductClosingContent;
  /** Para el botón: lo que se añade al carrito es el producto entero. */
  product: Product;
  price: ResolvedPrice | null;
  locale: Locale;
}

interface ClosingSkin {
  box: string;
  quote: string;
  /** El botón invierte los colores de la caja: fondo claro, letra oscura. */
  cta: string;
}

/**
 * Los tres temas del cierre, literales del original.
 *
 * `accent` es violeta con el botón en blanco; `dark` es `ink` con el
 * botón en violeta; `light` es `cream-2` con la letra oscura y el botón
 * en violeta con letra blanca — en el original ese último caso está
 * escrito como una excepción (`t.bg === "var(--cream-2)" ? "white" :
 * t.bg`) porque sin ella el botón saldría con la letra color crema sobre
 * violeta, que es 3.4:1.
 */
const CLOSING_SKINS: Record<ProductClosingTheme, ClosingSkin> = {
  accent: {
    box: "bg-gradient-to-br from-ds-violet-pop to-ds-indigo-deep shadow-ds-cta",
    quote: "text-white",
    cta: "bg-white text-ds-violet-pop hover:bg-white/90",
  },
  dark: {
    box: "bg-gradient-to-br from-ds-ink to-ds-indigo-night",
    quote: "text-white",
    cta: "bg-ds-violet-pop text-white hover:bg-ds-violet-pop/90",
  },
  light: {
    box: "bg-ds-cream-2",
    quote: "text-ds-ink",
    cta: "bg-ds-violet-pop text-white hover:bg-ds-violet-pop/90",
  },
};

/**
 * El cierre editorial: la frase grande y el botón de compra.
 *
 * ── El botón es del original, y hace falta ───────────────────────────
 * Aquí no estaba. La ficha con editorial completo mide seis pantallas, y
 * quien termina de leerla se queda al final sin nada que pulsar: el panel
 * de compra está pegado arriba en escritorio, pero en el teléfono queda a
 * cinco pantallas de distancia. El original cierra con "Agregar al
 * carrito · $1,630.00", y esa es la última frase de la página.
 *
 * ── Y por eso el precio aparece una TERCERA vez ──────────────────────
 * Portada, panel y cierre. Los tres salen del mismo `priceFor` y pasan
 * por `formatPrice`, así que no pueden discrepar; y los tres hacen algo
 * distinto —anunciar, operar, rematar—. Es lo que hace el original y se
 * sostiene mirándolo.
 *
 * ── Agotado: el botón se apaga, no desaparece ────────────────────────
 * Un cierre sin botón después de haberlo prometido durante seis
 * pantallas se lee como un fallo de carga. Apagado y diciendo "Agotado"
 * dice la verdad.
 *
 * ── Medidas del original ─────────────────────────────────────────────
 *   caja     radio 4 · padding 64/32 · centrado
 *   frase    SANS 300 · clamp(28px, 4vw, 44px) · 1.15 · -0.025em
 *            ancho máximo 720 · 32 px hasta el botón
 *   botón    cápsula · padding 14/32 · 13px/600 · 0.04em
 */
export function ProductClosing({
  closing,
  product,
  price,
  locale,
}: ProductClosingProps) {
  const { t } = useTranslation();
  const { add, open } = useCart();
  const skin = CLOSING_SKINS[closing.theme ?? "accent"];
  const soldOut = maxQuantityFor(product.stock) === 0;
  const [upsell, setUpsell] = useState(false);

  // Mismo CTA que el botón principal de la ficha: añadir abre la
  // invitación al paquete, no el carrito. Ver `ProductPurchase`.
  const addToCart = () => {
    add(product, 1);
    setUpsell(true);
  };

  return (
    <section className={cn("rounded-[36px] px-8 py-16 text-center sm:py-20", skin.box)}>
      <p
        className={cn(
          "mx-auto max-w-[720px] text-[clamp(28px,4vw,44px)] font-light leading-[1.15] tracking-[-0.025em]",
          skin.quote,
        )}
      >
        {closing.quote}
      </p>

      <button
        type="button"
        onClick={addToCart}
        disabled={soldOut}
        className={cn(
          "mt-8 rounded-ds-pill px-8 py-3.5 text-[13px] font-semibold tracking-[0.04em] transition",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
          "disabled:cursor-not-allowed disabled:opacity-60",
          skin.cta,
        )}
      >
        {soldOut || !price
          ? t("Agotado")
          : `${t("Añadir al carrito")} · ${formatPrice(price.cents, price.currency, numberLocaleOf(locale))}`}
      </button>

      {upsell && (
        <PackageUpsellDialog
          onClose={() => {
            setUpsell(false);
            open();
          }}
          onOnlyThisOrder={() => {
            setUpsell(false);
            open();
          }}
        />
      )}
    </section>
  );
}
